import type { DocumentSource } from '../models/viewer-types';

const DEFAULT_TIMEOUT_MS = 60000;
const URL_RELEASE_DELAY_MS = 1000;
const OVER_LIMIT = 'The download exceeds the configured file size limit.';

/** Where the original bytes come from: already in hand, behind a link, or inside an archive. */
export interface DownloadTarget {
  name: string;
  blob?: Blob;
  url?: string;
  source?: DocumentSource;
}

export interface DownloadActionOptions {
  getLimit: () => number;
  onError: (message: string) => void;
  timeoutMs?: number;
}

export interface DownloadAction {
  set(value: DownloadTarget | null): void;
  ready(): void;
  reset(): void;
  destroy(): void;
}

// A name a file system will take: no path, and nothing a control character could do to it.
function fileNameOf(name: string): string {
  return (
    (name.split(/[\\/]/).pop() as string)
      // eslint-disable-next-line no-control-regex
      .replace(/[\x00-\x1f\x7f]/g, '') || 'document'
  );
}

/**
 * Makes a button that saves the original bytes of what is on screen. It reads them anew rather than
 * from whatever the preview holds: a rotated image, a formatted document and a PDF handed to the
 * worker are all still saved as they arrived.
 *
 * @param {HTMLButtonElement} button The button to drive.
 * @param {DownloadActionOptions} options The size limit, where to report, and when to give up.
 * @return {DownloadAction} Handles to point the button at a document and to put it away.
 */
export function createDownloadAction(button: HTMLButtonElement, options: DownloadActionOptions): DownloadAction {
  const { getLimit, onError, timeoutMs = DEFAULT_TIMEOUT_MS } = options;
  const document = button.ownerDocument;

  let target: DownloadTarget | null = null;
  let controller: AbortController | null = null;
  let cleanupTimer: ReturnType<typeof setTimeout> | undefined;
  let objectUrl: string | null = null;
  let downloadTimer: ReturnType<typeof setTimeout> | null = null;

  function releaseUrl(): void {
    clearTimeout(cleanupTimer);

    if (objectUrl) URL.revokeObjectURL(objectUrl);

    objectUrl = null;
  }

  function reset(): void {
    controller?.abort();
    clearTimeout(downloadTimer as ReturnType<typeof setTimeout>);
    downloadTimer = null;
    controller = null;
    target = null;
    releaseUrl();
    button.hidden = true;
    button.disabled = false;
    button.removeAttribute('aria-busy');
    button.title = 'Download original file';
  }

  async function readUrl(url: string, signal: AbortSignal, limit: number): Promise<Blob> {
    const response = await fetch(url, { signal, credentials: 'omit', referrerPolicy: 'no-referrer' });

    if (!response.ok) throw Error(`Download failed: HTTP ${response.status}. Check the URL and access permissions.`);

    if (Number(response.headers.get('content-length')) > limit) {
      await response.body?.cancel();

      throw Error(OVER_LIMIT);
    }

    if (!response.body) throw Error('The server returned an empty download response.');

    const reader = response.body.getReader();
    const chunks: Array<Uint8Array> = [];
    let size = 0;

    try {
      for (;;) {
        const { done, value } = await reader.read();

        if (done) break;

        size += value.length;

        // Checked as the bytes arrive: a server may well answer more than its own header promised.
        if (size > limit) throw Error(OVER_LIMIT);

        chunks.push(value);
      }
    } catch (error) {
      await reader.cancel().catch(() => undefined);

      throw error;
    } finally {
      reader.releaseLock();
    }

    return new Blob(chunks as Array<BlobPart>, {
      type: response.headers.get('content-type') || 'application/octet-stream'
    });
  }

  async function bytesOf(current: DownloadTarget, signal: AbortSignal, limit: number): Promise<Blob> {
    if (current.blob) return current.blob;

    if (current.url) return readUrl(current.url, signal, limit);

    const source = current.source as DocumentSource;
    const bytes = await source.load(signal);

    if (!(bytes instanceof Uint8Array) || bytes.length !== source.size) {
      throw Error('The document source returned an invalid file size.');
    }

    return new Blob([bytes as BlobPart], { type: 'application/octet-stream' });
  }

  function save(blob: Blob, name: string): void {
    releaseUrl();
    objectUrl = URL.createObjectURL(blob);

    const link = document.createElement('a');

    link.href = objectUrl;
    link.download = fileNameOf(name);
    link.hidden = true;
    document.body.append(link);
    link.click();
    link.remove();

    cleanupTimer = setTimeout(releaseUrl, URL_RELEASE_DELAY_MS);
  }

  function messageOf(error: Error, aborted: boolean): string {
    if (aborted) return 'The download timed out. Please try again.';

    if (error.name === 'TypeError') return 'Unable to download the file. Check the URL, permissions, and CORS settings.';

    return error.message;
  }

  button.onclick = async (): Promise<void> => {
    if (!target || controller) return;

    const current = target;
    const active = new AbortController();

    controller = active;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    button.title = 'Downloading original file…';

    const timer = setTimeout(() => active.abort(), timeoutMs);

    downloadTimer = timer;

    try {
      const limit = getLimit();
      const blob = await bytesOf(current, active.signal, limit);

      if (active.signal.aborted) throw Error('The download timed out. Please try again.');

      // The reader moved on while the bytes were coming: what arrived is no longer what is on screen.
      if (target !== current) return;

      if (blob.size > limit) throw Error(OVER_LIMIT);

      save(blob, current.name);
    } catch (error) {
      if (target === current) onError(messageOf(error as Error, active.signal.aborted));
    } finally {
      clearTimeout(timer);

      if (downloadTimer === timer) downloadTimer = null;

      if (controller === active) {
        controller = null;
        button.disabled = false;
        button.removeAttribute('aria-busy');
        button.title = 'Download original file';
      }
    }
  };

  reset();

  return {
    set(value: DownloadTarget | null): void {
      target = value;
    },
    ready(): void {
      button.hidden = !target;
    },
    reset,
    destroy(): void {
      reset();
      button.onclick = null;
    }
  };
}
